/**
 * Select editable con catálogo gestionable (añadir/quitar opciones inline).
 * Se usa en RepairForm para `device_type`, `section`, `backup_status`, etc.
 * Extraído de `RepairForm.jsx`.
 */
import { useState } from "react";
import { Settings as Cog, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function EditableCatalogSelect({
  label, required, value, onChange, items, onItemsChange,
  placeholderAdd, placeholder, testid, testidManage, testidAdd,
  hideManage = false,
}) {
  const [managing, setManaging] = useState(false);
  const [newItem, setNewItem] = useState("");

  const add = async () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.includes(v)) {
      toast.info("Ya existe en el catálogo");
      return;
    }
    await onItemsChange([...items, v]);
    onChange(v);
    setNewItem("");
    toast.success(`"${v}" añadido`);
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Quitar "${it}" del catálogo?`)) return;
    await onItemsChange(items.filter((i) => i !== it));
    if (value === it) onChange("");
    toast.success(`"${it}" eliminado`);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
        {!hideManage && (
          <button
            type="button"
            onClick={() => setManaging(!managing)}
            data-testid={testidManage}
            className="text-[10px] font-mono uppercase tracking-wider text-[#6E8B3D] hover:underline inline-flex items-center gap-1"
          >
            <Cog size={11} /> {managing ? "Cerrar" : "Gestionar"}
          </button>
        )}
      </div>
      <select
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
      >
        <option value="">{placeholder || "— Sin asignar —"}</option>
        {items.map((i) => (
          <option key={i} value={i}>{i}</option>
        ))}
      </select>
      {managing && (
        <div className="mt-2 border border-[#6E8B3D] bg-[#F0F4E8] p-3 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Gestionar opciones
          </div>
          <div className="flex flex-wrap gap-1">
            {items.map((it) => (
              <span
                key={it}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#111110] text-xs"
              >
                {it}
                <button
                  type="button"
                  onClick={() => remove(it)}
                  className="hover:text-red-600"
                  title="Quitar"
                  aria-label={`Quitar ${it}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            {items.length === 0 && (
              <span className="text-xs text-[#525252]">Catálogo vacío</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder={placeholderAdd}
              data-testid={testidAdd}
              className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-2 py-1 text-xs rounded-none"
            />
            <button
              type="button"
              onClick={add}
              className="bg-[#111110] hover:bg-[#6E8B3D] text-white px-2 py-1 text-xs inline-flex items-center gap-1"
            >
              <Plus size={11} /> Añadir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
